import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/apiClient';
import GlancePillows from './GlancePillows';
import ActivityPills from '../../components/ActivityPills';
import MorningBriefs from '../../components/MorningBriefs';
import PMTiles from '../portal/PMTiles';

// Executive command center — a whole-company rollup rendered on the Executive
// landing: each PM's four pillows, the workflow status by type, and the shop queue.
const WF_META = {
  vertical_drilling:   { label: 'Vertical Drilling',  icon: '🕳️', unit: 'holes' },
  horizontal_drilling: { label: 'Directional Drilling', icon: '↔️', unit: 'bores' },
  loops:               { label: 'Loops',              icon: '🔁', unit: 'cards' },
  nve:                 { label: 'NVE',                 icon: '⚡', unit: 'meters' },
  usa_digs:            { label: 'USA Digs',            icon: '🚧', unit: 'tickets' },
  submittals:          { label: 'Submittals',         icon: '📋', unit: 'items' },
  permits:             { label: 'Permits',            icon: '📜', unit: 'permits' },
};
const wfMeta = t => WF_META[t] || { label: (t || 'Other').replace(/_/g, ' '), icon: '📁', unit: 'cards' };

const card = { background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12 };
const sectionLabel = { fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px' };

function Metric({ label, value, danger, onClick }) {
  return (
    <div onClick={onClick}
      style={{ background: 'var(--surface-alt, #f8fafc)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', cursor: onClick ? 'pointer' : 'default', display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, color: danger && value > 0 ? '#dc2626' : 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
        {label}{onClick && <span style={{ color: 'var(--text-muted)', opacity: 0.55, fontSize: 11 }}>›</span>}
      </div>
    </div>
  );
}

function PmStat({ icon, n, label, danger }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 52 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: danger && n > 0 ? '#dc2626' : 'var(--text)' }}>{icon} {n}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{label}</div>
    </div>
  );
}

export default function ExecOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/exec/summary').then(setData).catch(e => setError(e?.error || 'Failed to load the overview.'));
  }, []);

  if (error) return <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14, marginBottom: 20 }}>{error}</div>;
  if (!data) return <div style={{ color: 'var(--text-muted)', padding: '30px 0' }}>Loading the overview…</div>;

  const t = data.totals || {};
  const pms = data.pms || [];
  const workflows = data.workflows || [];
  const shop = data.shop || { new: 0, working: 0, complete: 0 };

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Company-wide activity feed + Scotto's AI summary */}
      <div style={{ marginBottom: 20 }}>
        <ActivityPills pageName="Company" summaryLabel="Company Update" scope="all" endpoint="/exec/activity" />
      </div>

      {/* The PM dashboard's six tiles, for the whole company or one PM (Pat, Oct 7 2026). */}
      <h2 style={sectionLabel}>Bids · Bills · Updates · Workflows · POs · Submittals</h2>
      <PMTiles pms={pms.map(p => ({ id: p.id, name: p.name }))} />

      {/* Morning Brief — the company podcast + rundown, right on the dashboard (the
          PM pages show theirs the same way; it also stays on the Executive tab). */}
      <h2 style={sectionLabel}>🎙️ Morning Brief</h2>
      <div style={{ marginBottom: 26 }}>
        <MorningBriefs audience="exec" />
      </div>

      {/* Company at a glance — click a pillow to expand its list */}
      <h2 style={sectionLabel}>Company at a Glance</h2>
      <GlancePillows totals={t} lists={data.lists} navigate={navigate} />

      {/* By Project Manager */}
      <h2 style={sectionLabel}>By Project Manager</h2>
      {pms.length === 0 ? (
        <div style={{ ...card, borderStyle: 'dashed', padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, marginBottom: 30 }}>No project managers found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 30 }}>
          {pms.map(p => (
            <div key={p.id} onClick={() => navigate(`/admin/pm/${p.id}`)}
              title="Open this PM's at-a-glance dashboard"
              style={{ ...card, padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 160 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#eef2ff', color: '#3730a3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>
                  {p.name.split(' ').map(x => x[0]).slice(0, 2).join('')}
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14.5, color: 'var(--text)' }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.activeJobs} active job{p.activeJobs !== 1 ? 's' : ''}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 18, flex: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                <PmStat icon="👷" n={p.crew} label="Crew" />
                <PmStat icon="🔧" n={p.equipment} label="Equip" />
                <PmStat icon="🚚" n={p.material} label="Material" />
                <PmStat icon="📄" n={p.bids} label="Bids" />
                <PmStat icon="⏰" n={p.overdue} label="Overdue" danger />
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: 18 }}>›</span>
            </div>
          ))}
        </div>
      )}

      {/* Work flows */}
      <h2 style={sectionLabel}>Work Flows</h2>
      {workflows.length === 0 ? (
        <div style={{ ...card, borderStyle: 'dashed', padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, marginBottom: 30 }}>No workflow cards on active jobs yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 30 }}>
          {workflows.map(w => {
            const m = wfMeta(w.type);
            const pct = w.total ? Math.round((w.complete / w.total) * 100) : 0;
            return (
              <div key={w.type} onClick={() => navigate('/admin/workflows')}
                style={{ ...card, padding: '13px 15px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>{m.icon} {m.label}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{w.complete}/{w.total} {m.unit}</span>
                </div>
                <div style={{ height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: '#1e3a5f' }} />
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>{w.active} in progress · {pct}% complete</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Shop */}
      <h2 style={sectionLabel}>Shop</h2>
      <div onClick={() => navigate('/admin/shop')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 12, cursor: 'pointer' }}>
        <Metric label="New requests" value={shop.new || 0} />
        <Metric label="Working on" value={shop.working || 0} />
        <Metric label="Completed" value={shop.complete || 0} />
      </div>
    </div>
  );
}
